import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getCustomTerminalSessions,
  addCustomTerminalSession,
  removeCustomTerminalSession,
  updateTerminalSession,
  setTerminalSessions,
  DEFAULT_TERMINAL_SESSIONS,
  CustomTerminalSession,
} from '../services/userPreferences';

interface TmuxLiveStatus {
  name: string;
  created: number;
  windows: number;
  panes?: number;
  attached: number;
  lastActivity?: number;
  cpu?: number;
  memory?: number;
  commands?: string[];
  gitBranch?: string | null;
}

interface ServerStats {
  hostname?: string;
  cpu?: number | null;
  cpuCores?: number;
  loadAvg?: number[];
  memory?: { total: number; used: number; available: number } | null;
  disk?: { total: number; used: number; percent: number } | null;
  uptime?: number;
}

// Sparkline history for CPU/memory
interface ResourceHistory {
  [sessionName: string]: { cpu: number[]; memory: number[] };
}

// Available tags
const AVAILABLE_TAGS = ['frontend', 'backend', 'api', 'database', 'devops', 'testing', 'docs'];

// Log audit event
async function logAuditEvent(action: string, details: string) {
  try {
    await fetch('/api/admin/audit-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, details }),
    });
  } catch (err) {
    console.error('Failed to log audit event:', err);
  }
}

type SortField = 'displayName' | 'tmuxName' | 'cpu' | 'memory' | 'lastAccessed';
type SortDir = 'asc' | 'desc';
type FilterStatus = 'all' | 'active' | 'attached' | 'inactive';

function formatUptime(createdTimestamp: number): string {
  const now = Date.now() / 1000;
  const seconds = Math.floor(now - createdTimestamp);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${remainingMinutes}m`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
}

function formatServerUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)}G`;
  return `${Math.round(mb)}M`;
}

function formatLastAccessed(timestamp?: number): string {
  if (!timestamp) return 'Never';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getUsageColor(percent: number): string {
  if (percent >= 80) return 'critical';
  if (percent >= 60) return 'warning';
  return 'healthy';
}

// Mini sparkline component
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 40},${20 - (v / max) * 18}`).join(' ');
  return (
    <svg width="40" height="20" className="sparkline">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

export default function AdminTerminal() {
  const statusApiUrl = 'https://jmgdev.tail68ddf4.ts.net';

  // Core state
  const [sessions, setSessions] = useState<CustomTerminalSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [liveStatus, setLiveStatus] = useState<Record<string, TmuxLiveStatus>>({});
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [resourceHistory, setResourceHistory] = useState<ResourceHistory>({});

  // UI state
  const [sortField, setSortField] = useState<SortField>('displayName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterTag, setFilterTag] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  // Edit state
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editTmuxName, setEditTmuxName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);

  // Add session state
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newTmuxName, setNewTmuxName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Resource alert threshold
  const CPU_ALERT_THRESHOLD = 50;
  const MEM_ALERT_THRESHOLD = 50;

  // Fetch live status
  const fetchLiveStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const response = await fetch(`${statusApiUrl}/tmux-api`);
      if (response.ok) {
        const data = await response.json();
        if (data.sessions) {
          const statusMap: Record<string, TmuxLiveStatus> = {};
          data.sessions.forEach((s: TmuxLiveStatus) => {
            statusMap[s.name] = s;
          });
          setLiveStatus(statusMap);

          // Update resource history for sparklines
          setResourceHistory(prev => {
            const updated = { ...prev };
            data.sessions.forEach((s: TmuxLiveStatus) => {
              if (!updated[s.name]) {
                updated[s.name] = { cpu: [], memory: [] };
              }
              updated[s.name].cpu = [...updated[s.name].cpu.slice(-19), s.cpu || 0];
              updated[s.name].memory = [...updated[s.name].memory.slice(-19), s.memory || 0];
            });
            return updated;
          });
        }
        if (data.server) {
          setServerStats(data.server);
        }
      } else {
        setStatusError('Unable to reach server');
      }
    } catch (err) {
      console.error('Failed to fetch tmux status:', err);
      setStatusError('Connection failed');
    } finally {
      setStatusLoading(false);
    }
  }, [statusApiUrl]);

  // Load sessions
  useEffect(() => {
    getCustomTerminalSessions()
      .then(async (loadedSessions) => {
        if (loadedSessions.length === 0) {
          const seeded = await setTerminalSessions(DEFAULT_TERMINAL_SESSIONS);
          setSessions(seeded);
        } else {
          setSessions(loadedSessions);
        }
      })
      .catch((err) => {
        console.error('Failed to load sessions:', err);
        setSessions(DEFAULT_TERMINAL_SESSIONS);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Auto-refresh
  useEffect(() => {
    fetchLiveStatus();
    if (autoRefresh) {
      const interval = setInterval(fetchLiveStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchLiveStatus, autoRefresh]);

  // Filter and sort sessions
  const filteredSessions = useMemo(() => {
    let result = [...sessions];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.displayName.toLowerCase().includes(q) ||
        s.tmuxName.toLowerCase().includes(q) ||
        s.notes?.toLowerCase().includes(q) ||
        s.tags?.some(t => t.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      result = result.filter(s => {
        const status = liveStatus[s.tmuxName];
        if (filterStatus === 'active') return !!status;
        if (filterStatus === 'attached') return status?.attached > 0;
        if (filterStatus === 'inactive') return !status;
        return true;
      });
    }

    // Tag filter
    if (filterTag) {
      result = result.filter(s => s.tags?.includes(filterTag));
    }

    // Pinned only
    if (showPinnedOnly) {
      result = result.filter(s => s.pinned);
    }

    // Sort - pinned always first
    result.sort((a, b) => {
      // Pinned first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      let cmp: number;
      if (sortField === 'cpu' || sortField === 'memory') {
        const aStatus = liveStatus[a.tmuxName];
        const bStatus = liveStatus[b.tmuxName];
        const aVal = aStatus?.[sortField] ?? -1;
        const bVal = bStatus?.[sortField] ?? -1;
        cmp = aVal - bVal;
      } else if (sortField === 'lastAccessed') {
        cmp = (a.lastAccessed || 0) - (b.lastAccessed || 0);
      } else {
        cmp = a[sortField].localeCompare(b[sortField], undefined, { sensitivity: 'base' });
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [sessions, searchQuery, filterStatus, filterTag, showPinnedOnly, sortField, sortDir, liveStatus]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'cpu' || field === 'memory' || field === 'lastAccessed' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '↕';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  const openSession = async (tmuxName: string, displayName: string) => {
    // Update last accessed
    const updated = await updateTerminalSession(tmuxName, { lastAccessed: Date.now() });
    setSessions(updated);

    logAuditEvent('terminal:open', `Opened: ${displayName} (${tmuxName})`);
    const url = `/terminal?session=${encodeURIComponent(displayName)}&tmux=${encodeURIComponent(tmuxName)}`;
    window.open(url, `terminal-${tmuxName}`, 'width=1200,height=800');
  };

  const togglePin = async (tmuxName: string) => {
    const session = sessions.find(s => s.tmuxName === tmuxName);
    if (!session) return;
    const updated = await updateTerminalSession(tmuxName, { pinned: !session.pinned });
    setSessions(updated);
  };

  const addSession = async () => {
    const display = newDisplayName.trim();
    const tmux = newTmuxName.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!display || !tmux) return;
    if (sessions.some(s => s.tmuxName === tmux)) {
      alert('Session name already exists');
      return;
    }
    try {
      const updated = await addCustomTerminalSession({ displayName: display, tmuxName: tmux });
      setSessions(updated);
      setNewDisplayName('');
      setNewTmuxName('');
      setShowAddForm(false);
      logAuditEvent('terminal:create', `Created: ${display} (${tmux})`);
    } catch (err) {
      console.error('Failed to add session:', err);
    }
  };

  const deleteSession = async (tmuxName: string, displayName: string) => {
    if (!confirm(`Delete "${displayName}"?`)) return;
    try {
      const updated = await removeCustomTerminalSession(tmuxName);
      setSessions(updated);
      logAuditEvent('terminal:remove', `Removed: ${displayName} (${tmuxName})`);
    } catch (err) {
      console.error('Failed to remove session:', err);
    }
  };

  const startEdit = (session: CustomTerminalSession) => {
    setEditingSession(session.tmuxName);
    setEditDisplayName(session.displayName);
    setEditTmuxName(session.tmuxName);
    setEditNotes(session.notes || '');
    setEditTags(session.tags || []);
  };

  const cancelEdit = () => {
    setEditingSession(null);
  };

  const saveEdit = async (oldTmuxName: string) => {
    const display = editDisplayName.trim();
    const tmux = editTmuxName.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!display || !tmux) return;
    if (tmux !== oldTmuxName && sessions.some(s => s.tmuxName === tmux)) {
      alert('Session name already exists');
      return;
    }
    try {
      const updated = await updateTerminalSession(oldTmuxName, {
        displayName: display,
        tmuxName: tmux,
        notes: editNotes || undefined,
        tags: editTags.length > 0 ? editTags : undefined,
      });
      setSessions(updated);
      setEditingSession(null);
      logAuditEvent('terminal:edit', `Edited: ${display} (${tmux})`);
    } catch (err) {
      console.error('Failed to edit session:', err);
    }
  };

  const toggleEditTag = (tag: string) => {
    setEditTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const resetToDefaults = async () => {
    if (!confirm('Reset all sessions to defaults?')) return;
    try {
      const reset = await setTerminalSessions(DEFAULT_TERMINAL_SESSIONS);
      setSessions(reset);
      logAuditEvent('terminal:reset', 'Reset to defaults');
    } catch (err) {
      console.error('Failed to reset:', err);
    }
  };

  // Counts
  const activeSessions = Object.keys(liveStatus).length;
  const attachedSessions = Object.values(liveStatus).filter(s => s.attached > 0).length;
  const alertSessions = Object.values(liveStatus).filter(s =>
    (s.cpu || 0) > CPU_ALERT_THRESHOLD || (s.memory || 0) > MEM_ALERT_THRESHOLD
  ).length;

  // All used tags
  const usedTags = useMemo(() => {
    const tags = new Set<string>();
    sessions.forEach(s => s.tags?.forEach(t => tags.add(t)));
    return Array.from(tags);
  }, [sessions]);

  return (
    <div className="settings-page terminal-page">
      {/* Server Stats Bar */}
      {serverStats && !statusError && (
        <div className="server-stats-bar">
          <div className="server-stat" title={`${serverStats.cpuCores || '?'} cores`}>
            <span className="stat-label">CPU</span>
            <span className={`stat-value ${getUsageColor(serverStats.cpu || 0)}`}>
              {serverStats.cpu != null ? `${serverStats.cpu}%` : '--'}
            </span>
            <div className="stat-bar-mini">
              <div className={`stat-bar-fill ${getUsageColor(serverStats.cpu || 0)}`} style={{ width: `${serverStats.cpu || 0}%` }} />
            </div>
          </div>
          <div className="server-stat" title={serverStats.memory ? `${formatMB(serverStats.memory.used)} / ${formatMB(serverStats.memory.total)}` : ''}>
            <span className="stat-label">Memory</span>
            <span className={`stat-value ${serverStats.memory ? getUsageColor((serverStats.memory.used / serverStats.memory.total) * 100) : ''}`}>
              {serverStats.memory ? `${Math.round((serverStats.memory.used / serverStats.memory.total) * 100)}%` : '--'}
            </span>
            <div className="stat-bar-mini">
              <div className={`stat-bar-fill ${serverStats.memory ? getUsageColor((serverStats.memory.used / serverStats.memory.total) * 100) : ''}`} style={{ width: serverStats.memory ? `${(serverStats.memory.used / serverStats.memory.total) * 100}%` : '0%' }} />
            </div>
          </div>
          <div className="server-stat" title={serverStats.disk ? `${formatMB(serverStats.disk.used)} / ${formatMB(serverStats.disk.total)}` : ''}>
            <span className="stat-label">Disk</span>
            <span className={`stat-value ${serverStats.disk ? getUsageColor(serverStats.disk.percent) : ''}`}>
              {serverStats.disk ? `${serverStats.disk.percent}%` : '--'}
            </span>
            <div className="stat-bar-mini">
              <div className={`stat-bar-fill ${serverStats.disk ? getUsageColor(serverStats.disk.percent) : ''}`} style={{ width: serverStats.disk ? `${serverStats.disk.percent}%` : '0%' }} />
            </div>
          </div>
          <div className="server-stat">
            <span className="stat-label">Sessions</span>
            <span className="stat-value">
              {activeSessions} <span className="stat-sub">active</span>
              {attachedSessions > 0 && <>{attachedSessions} <span className="stat-sub">attached</span></>}
            </span>
            {alertSessions > 0 && (
              <span className="stat-alert" title={`${alertSessions} session(s) with high resource usage`}>
                ⚠ {alertSessions} alert{alertSessions > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="server-stat server-uptime">
            <span className="stat-label">Uptime</span>
            <span className="stat-value">{serverStats.uptime ? formatServerUptime(serverStats.uptime) : '--'}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="settings-section">
        <div className="sessions-header">
          <div className="sessions-header-left">
            <h2>Terminal Sessions</h2>
          </div>
          <div className="sessions-header-right">
            <label className="auto-refresh-toggle" title="Auto-refresh every 10s">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              <span>Auto</span>
            </label>
            <button className="button-secondary button-sm" onClick={fetchLiveStatus} disabled={statusLoading}>
              {statusLoading ? '...' : 'Refresh'}
            </button>
            <Link to="/admin/audit-log" className="terminal-audit-link">Audit Log →</Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="terminal-filters">
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="terminal-search"
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as FilterStatus)} className="terminal-filter-select">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="attached">Attached</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className="terminal-filter-select">
            <option value="">All Tags</option>
            {usedTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
          <label className="filter-checkbox" title="Show only pinned sessions">
            <input type="checkbox" checked={showPinnedOnly} onChange={(e) => setShowPinnedOnly(e.target.checked)} />
            <span>★ Pinned</span>
          </label>
          <button className="button-primary button-sm" onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? '− Cancel' : '+ Add'}
          </button>
        </div>

        {/* Add Session Form */}
        {showAddForm && (
          <div className="terminal-add-inline">
            <input type="text" placeholder="Display Name" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} className="terminal-input" />
            <input type="text" placeholder="tmux-name" value={newTmuxName} onChange={(e) => setNewTmuxName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} className="terminal-input mono" />
            <button className="button-primary" onClick={addSession} disabled={!newDisplayName.trim() || !newTmuxName.trim()}>Create</button>
          </div>
        )}

        {statusError && <div className="terminal-status-error-banner">{statusError}</div>}

        {/* Sessions Table */}
        {isLoading ? (
          <p className="terminal-loading">Loading...</p>
        ) : (
          <div className="terminal-table-wrapper">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th className={`sortable${sortField === 'displayName' ? ' sorted' : ''}`} onClick={() => toggleSort('displayName')}>
                    Project {sortIndicator('displayName')}
                  </th>
                  <th className={`sortable${sortField === 'tmuxName' ? ' sorted' : ''}`} onClick={() => toggleSort('tmuxName')}>
                    Session {sortIndicator('tmuxName')}
                  </th>
                  <th>Status</th>
                  <th className={`sortable col-resource${sortField === 'cpu' ? ' sorted' : ''}`} onClick={() => toggleSort('cpu')}>
                    CPU {sortIndicator('cpu')}
                  </th>
                  <th className={`sortable col-resource${sortField === 'memory' ? ' sorted' : ''}`} onClick={() => toggleSort('memory')}>
                    Mem {sortIndicator('memory')}
                  </th>
                  <th className={`sortable col-accessed${sortField === 'lastAccessed' ? ' sorted' : ''}`} onClick={() => toggleSort('lastAccessed')}>
                    Accessed {sortIndicator('lastAccessed')}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.length === 0 ? (
                  <tr><td colSpan={8} className="no-results">No sessions match your filters</td></tr>
                ) : filteredSessions.map((session) => {
                  const status = liveStatus[session.tmuxName];
                  const isActive = !!status;
                  const isAttached = status?.attached > 0;
                  const isEditing = editingSession === session.tmuxName;
                  const isExpanded = expandedSession === session.tmuxName;
                  const history = resourceHistory[session.tmuxName];
                  const hasAlert = isActive && ((status.cpu || 0) > CPU_ALERT_THRESHOLD || (status.memory || 0) > MEM_ALERT_THRESHOLD);

                  if (isEditing) {
                    return (
                      <tr key={session.tmuxName} className="session-editing">
                        <td></td>
                        <td>
                          <input type="text" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} className="terminal-input-inline" autoFocus />
                        </td>
                        <td>
                          <input type="text" value={editTmuxName} onChange={(e) => setEditTmuxName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} className="terminal-input-inline mono" />
                        </td>
                        <td colSpan={4}>
                          <div className="edit-extras">
                            <input type="text" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Notes..." className="terminal-input-inline" />
                            <div className="edit-tags">
                              {AVAILABLE_TAGS.map(tag => (
                                <button key={tag} className={`tag-btn ${editTags.includes(tag) ? 'active' : ''}`} onClick={() => toggleEditTag(tag)}>{tag}</button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="session-actions">
                          <button className="button-primary button-sm" onClick={() => saveEdit(session.tmuxName)}>Save</button>
                          <button className="button-secondary button-sm" onClick={cancelEdit}>Cancel</button>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <>
                      <tr
                        key={session.tmuxName}
                        className={`${isActive ? 'session-active' : ''} ${isExpanded ? 'session-expanded' : ''} ${hasAlert ? 'session-alert' : ''}`}
                        onClick={() => setExpandedSession(isExpanded ? null : session.tmuxName)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="session-pin" onClick={(e) => { e.stopPropagation(); togglePin(session.tmuxName); }}>
                          <span className={session.pinned ? 'pinned' : ''} title={session.pinned ? 'Unpin' : 'Pin'}>★</span>
                        </td>
                        <td className="session-name">
                          {session.displayName}
                          {status?.gitBranch && <span className="session-branch">{status.gitBranch}</span>}
                          {session.tags && session.tags.length > 0 && (
                            <span className="session-tags">
                              {session.tags.map(t => <span key={t} className="session-tag">{t}</span>)}
                            </span>
                          )}
                        </td>
                        <td className="session-tmux">{session.tmuxName}</td>
                        <td className="session-status-cell">
                          {isActive ? (
                            <span className={`session-status-badge ${isAttached ? 'attached' : 'running'}`}>
                              <span className={`session-status-dot ${isAttached ? 'attached' : 'detached'}`} />
                              <span className="session-uptime">{formatUptime(status.created)}</span>
                              <span className="session-windows">{status.windows}w{status.panes ? `/${status.panes}p` : ''}</span>
                            </span>
                          ) : (
                            <span className="session-status-badge inactive">Inactive</span>
                          )}
                        </td>
                        <td className="session-resource">
                          {isActive && status.cpu != null ? (
                            <div className="resource-with-spark">
                              <span className={`resource-value ${getUsageColor(status.cpu * 5)}`}>{status.cpu.toFixed(1)}%</span>
                              {history && <Sparkline data={history.cpu} color={status.cpu > CPU_ALERT_THRESHOLD ? '#ef4444' : '#22c55e'} />}
                            </div>
                          ) : <span className="resource-value inactive">--</span>}
                        </td>
                        <td className="session-resource">
                          {isActive && status.memory != null ? (
                            <div className="resource-with-spark">
                              <span className={`resource-value ${getUsageColor(status.memory * 5)}`}>{status.memory.toFixed(1)}%</span>
                              {history && <Sparkline data={history.memory} color={status.memory > MEM_ALERT_THRESHOLD ? '#ef4444' : '#3b82f6'} />}
                            </div>
                          ) : <span className="resource-value inactive">--</span>}
                        </td>
                        <td className="session-accessed">{formatLastAccessed(session.lastAccessed)}</td>
                        <td className="session-actions" onClick={(e) => e.stopPropagation()}>
                          <button className="button-primary button-sm" onClick={() => openSession(session.tmuxName, session.displayName)}>Open</button>
                          <button className="button-secondary button-sm" onClick={() => startEdit(session)}>Edit</button>
                          <button className="button-icon-danger" onClick={() => deleteSession(session.tmuxName, session.displayName)}>×</button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${session.tmuxName}-details`} className="session-details-row">
                          <td colSpan={8}>
                            <div className="session-details">
                              {session.notes && (
                                <div className="detail-item detail-notes">
                                  <span className="detail-label">Notes</span>
                                  <span className="detail-value">{session.notes}</span>
                                </div>
                              )}
                              {isActive && (
                                <>
                                  <div className="detail-item">
                                    <span className="detail-label">Last Activity</span>
                                    <span className="detail-value">{status.lastActivity ? formatUptime(status.lastActivity) + ' ago' : 'Unknown'}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="detail-label">Windows / Panes</span>
                                    <span className="detail-value">{status.windows} / {status.panes || status.windows}</span>
                                  </div>
                                  {status.commands && status.commands.length > 0 && (
                                    <div className="detail-item">
                                      <span className="detail-label">Commands</span>
                                      <span className="detail-value detail-commands">
                                        {status.commands.map((cmd, i) => <code key={i}>{cmd}</code>)}
                                      </span>
                                    </div>
                                  )}
                                  <div className="detail-item">
                                    <span className="detail-label">Resources</span>
                                    <span className="detail-value">CPU: {status.cpu?.toFixed(1) || 0}% · Mem: {status.memory?.toFixed(1) || 0}%</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="terminal-footer">
          <span className="session-count">{filteredSessions.length} of {sessions.length} sessions</span>
          <button className="link-button" onClick={resetToDefaults}>Reset to defaults</button>
        </div>
      </section>

      {/* Quick Reference */}
      <section className="settings-section terminal-help">
        <details>
          <summary>Quick Reference</summary>
          <div className="terminal-help-grid">
            <div className="help-item"><code>Ctrl+b [</code><span>Scroll mode</span></div>
            <div className="help-item"><code>Ctrl+b d</code><span>Detach</span></div>
            <div className="help-item"><code>Ctrl+b c</code><span>New window</span></div>
            <div className="help-item"><code>Ctrl+b n/p</code><span>Next/prev</span></div>
            <div className="help-item"><code>Ctrl+b %</code><span>Split vert</span></div>
            <div className="help-item"><code>Ctrl+b "</code><span>Split horiz</span></div>
          </div>
          <div className="help-command-block">
            <code className="help-command">Ctrl+b : capture-pane -pS -2000 &gt; ~/output.txt</code>
            <span className="help-command-label">Save buffer to file</span>
          </div>
        </details>
      </section>
    </div>
  );
}
